import { useState } from 'react'
import { Upload, X, Loader2 } from 'lucide-react'
import { Modal } from '@/components/ui/modal'
import Button from '@/components/ui/button/Button'
import { productsApi } from '@/api/products'
import { useCreateProductAdmin, useCreateProductCategory } from '@/lib/productCatalog'
import { userMessage } from '@/api/errors'
import type { Category, ProductInput, ProductStatus } from '@/types/ecommerce'

const REQUIRED_COLUMNS = ['name', 'sku', 'price', 'quantity', 'category']
const KNOWN_COLUMNS = [...REQUIRED_COLUMNS, 'compareAtPrice', 'minStockLevel', 'tags', 'isFeatured', 'status', 'description']

/**
 * Minimal RFC4180-ish line splitter — handles quoted fields containing commas, doesn't handle
 * embedded newlines inside a quoted field (good enough for a product catalog CSV; a hand-rolled
 * parser matches this codebase's existing "no fancy dependency" call on downloadCsv rather than
 * pulling in papaparse for one feature).
 */
function parseCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"'
        i++
      } else if (ch === '"') {
        inQuotes = false
      } else {
        cur += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      out.push(cur)
      cur = ''
    } else {
      cur += ch
    }
  }
  out.push(cur)
  return out.map((s) => s.trim())
}

interface ParsedRow {
  name: string
  sku: string
  price: string
  quantity: string
  category: string
  compareAtPrice: string
  minStockLevel: string
  tags: string
  isFeatured: string
  status: string
  description: string
}

function parseCsv(text: string): { rows: ParsedRow[]; error: string | null } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0)
  if (lines.length < 2) return { rows: [], error: 'Paste a header row plus at least one data row.' }

  const headers = parseCsvLine(lines[0]).map((h) => h.toLowerCase())
  const missing = REQUIRED_COLUMNS.filter((c) => !headers.includes(c.toLowerCase()))
  if (missing.length > 0) return { rows: [], error: `Missing required column(s): ${missing.join(', ')}` }

  const rows = lines.slice(1).map((line) => {
    const cells = parseCsvLine(line)
    const byHeader: Record<string, string> = {}
    headers.forEach((h, i) => (byHeader[h] = cells[i] ?? ''))
    return {
      name: byHeader.name ?? '',
      sku: byHeader.sku ?? '',
      price: byHeader.price ?? '',
      quantity: byHeader.quantity ?? '',
      category: byHeader.category ?? '',
      compareAtPrice: byHeader.compareatprice ?? '',
      minStockLevel: byHeader.minstocklevel ?? '',
      tags: byHeader.tags ?? '',
      isFeatured: byHeader.isfeatured ?? '',
      status: byHeader.status ?? '',
      description: byHeader.description ?? '',
    }
  })
  return { rows, error: null }
}

type RowResult = { status: 'pending' | 'created' | 'skipped' | 'failed'; message?: string }

interface ProductImportModalProps {
  isOpen: boolean
  onClose: () => void
  categories: Category[]
}

/**
 * Real CSV product import — adapted from scripts/seed-scraped-products.mjs's proven flow
 * (login already handled by the admin session; category resolve-or-create; SKU-dedup check;
 * one POST /api/v1/products per row), just driven from a pasted/uploaded CSV instead of
 * scraped JSON. No new backend endpoint: this loops the existing single-product create hook
 * client-side, same "frontend-only, reuse what's real" call Export & Reports made — a true
 * bulk-import endpoint doesn't exist and this doesn't fake one.
 */
export default function ProductImportModal({ isOpen, onClose, categories }: ProductImportModalProps) {
  const [csvText, setCsvText] = useState('')
  const [rows, setRows] = useState<ParsedRow[] | null>(null)
  const [parseError, setParseError] = useState('')
  const [results, setResults] = useState<RowResult[]>([])
  const [importing, setImporting] = useState(false)

  const createProduct = useCreateProductAdmin()
  const createCategory = useCreateProductCategory()

  const reset = () => {
    setCsvText('')
    setRows(null)
    setParseError('')
    setResults([])
    setImporting(false)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleFile = async (file: File | undefined) => {
    if (!file) return
    setCsvText(await file.text())
  }

  const handlePreview = () => {
    const { rows: parsed, error } = parseCsv(csvText)
    setParseError(error ?? '')
    setRows(error ? null : parsed)
    setResults(parsed.map(() => ({ status: 'pending' })))
  }

  const runImport = async () => {
    if (!rows) return
    setImporting(true)

    // Local, mutable copy of categories so a category created for row 1 is found by row 5
    // without waiting on a query refetch.
    const categoryByName = new Map(categories.map((c) => [c.name.toLowerCase(), c.id]))
    const nextResults = [...results]

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      nextResults[i] = { status: 'pending' }
      setResults([...nextResults])

      try {
        if (!row.name || !row.sku || !row.price || !row.category) {
          throw new Error('Missing a required field (name/sku/price/category)')
        }

        let categoryId = categoryByName.get(row.category.trim().toLowerCase())
        if (!categoryId) {
          const created = await createCategory.mutateAsync({ name: row.category.trim() })
          categoryId = created.id
          categoryByName.set(row.category.trim().toLowerCase(), created.id)
        }

        const existing = await productsApi.list({ search: row.sku.trim(), pageSize: 5 })
        const duplicate = existing.items.find((p) => p.sku.toLowerCase() === row.sku.trim().toLowerCase())
        if (duplicate) {
          nextResults[i] = { status: 'skipped', message: `SKU "${row.sku}" already exists` }
          setResults([...nextResults])
          continue
        }

        const body: ProductInput = {
          name: row.name.trim(),
          sku: row.sku.trim(),
          price: Number(row.price),
          compareAtPrice: row.compareAtPrice ? Number(row.compareAtPrice) : null,
          quantity: Number(row.quantity) || 0,
          minStockLevel: row.minStockLevel ? Number(row.minStockLevel) : 5,
          status: (['Draft', 'Published', 'Archived'].includes(row.status) ? row.status : 'Draft') as ProductStatus,
          categoryIds: [categoryId],
          tags: row.tags ? row.tags.split(';').map((t) => t.trim()).filter(Boolean) : [],
          isFeatured: row.isFeatured.trim().toLowerCase() === 'true',
          description: row.description || undefined,
        }
        await createProduct.mutateAsync(body)
        nextResults[i] = { status: 'created' }
      } catch (err) {
        nextResults[i] = { status: 'failed', message: userMessage(err) }
      }
      setResults([...nextResults])
    }

    setImporting(false)
  }

  const counts = results.reduce(
    (acc, r) => ({ ...acc, [r.status]: (acc[r.status] ?? 0) + 1 }),
    {} as Record<string, number>,
  )
  const done = rows && results.length > 0 && results.every((r) => r.status !== 'pending')

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="max-w-3xl">
      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white p-6 dark:bg-gray-900">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h4 className="text-xl font-semibold text-gray-800 dark:text-white/90">Import products from CSV</h4>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Columns: <code className="text-xs">{KNOWN_COLUMNS.join(', ')}</code> (name/sku/price/quantity/category required). Category is resolved by name — created if it doesn't exist yet. Rows with a SKU that already exists are skipped, not overwritten.
            </p>
          </div>
          <button onClick={handleClose} className="rounded-full p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="h-4 w-4" />
          </button>
        </div>

        {!rows ? (
          <div className="space-y-3">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:border-gray-400 dark:border-gray-600 dark:text-gray-300">
              <Upload className="h-4 w-4" /> Upload a .csv file
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
            </label>
            <p className="text-center text-xs text-gray-400">— or paste CSV text below —</p>
            <textarea
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder={'name,sku,price,quantity,category\nBrown Sesame Seeds,BSS-013,4.99,20,Groceries'}
              rows={8}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
            {parseError && <p className="text-sm text-red-600 dark:text-red-400">{parseError}</p>}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
              <Button type="button" disabled={!csvText.trim()} onClick={handlePreview}>Preview</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  <tr>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">SKU</th>
                    <th className="px-3 py-2">Price</th>
                    <th className="px-3 py-2">Category</th>
                    <th className="px-3 py-2">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map((row, i) => (
                    <tr key={i}>
                      <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">{row.name}</td>
                      <td className="px-3 py-1.5 font-mono text-gray-500">{row.sku}</td>
                      <td className="px-3 py-1.5 text-gray-600 dark:text-gray-300">{row.price}</td>
                      <td className="px-3 py-1.5 text-gray-600 dark:text-gray-300">{row.category}</td>
                      <td className="px-3 py-1.5">
                        {results[i]?.status === 'pending' && importing ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                        ) : results[i]?.status === 'created' ? (
                          <span className="text-green-600 dark:text-green-400">Created</span>
                        ) : results[i]?.status === 'skipped' ? (
                          <span className="text-amber-600 dark:text-amber-400" title={results[i].message}>Skipped</span>
                        ) : results[i]?.status === 'failed' ? (
                          <span className="text-red-600 dark:text-red-400" title={results[i].message}>Failed</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {done && (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {counts.created ?? 0} created · {counts.skipped ?? 0} skipped · {counts.failed ?? 0} failed
              </p>
            )}

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={reset} disabled={importing}>Back</Button>
              {done ? (
                <Button type="button" onClick={handleClose}>Done</Button>
              ) : (
                <Button type="button" loading={importing} disabled={importing} onClick={runImport}>
                  Import {rows.length} row{rows.length === 1 ? '' : 's'}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
